/**
 * 条件求值器 —— 把实验包里的声明式条件字符串编译成判断函数。
 * 对应技术设计 §6.2「规则使用声明式 JSON，运行期编译成条件判断函数」。
 *
 * 【为什么是固定小文法，不是通用表达式引擎】
 * 实验包由内容团队（将来还有 Engine Agent）编写，条件必须**可静态校验**：
 * `pack-validate` 要在出包前就判断「这条条件引用的器材/端口/事件存不存在」。
 * 通用表达式引擎做不到这一点，还会把任意代码执行面带进实验包（§22 红线 15 的同类风险）。
 *
 * 【解析失败必须抛错】
 * 不能静默当 false。否则实验包写错一个字，表现出来是「这一步永远过不了」，
 * 学生卡死、作者无从排查 —— 这是最难定位的一类失败。
 *
 * 文法：
 *   条件   := 取值 比较符 字面量 | 谓词
 *   取值   := object(<id>).state.<路径>
 *           | state(<域>.<键>)
 *           | process(<id>).state
 *   谓词   := relation.exists(<端点>, <端点>[, <类型>])
 *           | relation.pathExists(<端点>, <端点>[, <介质>])
 *           | event.exists(<事件名>)
 *   比较符 := == | != | >= | <= | > | < | includes
 *   端点   := <器材id>[.<端口或槽位id>]
 */

import type { AssemblyGraph } from "../eom/assembly-graph.js";
import { hasRelation, listRelations } from "../eom/assembly-graph.js";
import type { Endpoint, RelationType } from "../eom/relation.js";
import { RELATION_TYPES } from "../eom/relation.js";
import type { ProcessTable } from "./process-engine.js";

/** 求值上下文：一次判定所需的全部只读事实。 */
export interface EvalContext {
  graph: AssemblyGraph;
  /** 引擎级状态域，如 `system.airTightness`（见 process-engine / safety-engine） */
  state: Readonly<Record<string, Record<string, unknown>>>;
  /** 反应过程状态，如 `oxygen_generation` → "running"（§6.4） */
  processes: Readonly<ProcessTable>;
  /** 本次运行已发生的事件名集合（§6.2 `event.exists`） */
  events: ReadonlySet<string>;
}

export type CompiledCondition = (context: EvalContext) => boolean;

export class ConditionSyntaxError extends Error {
  constructor(
    readonly source: string,
    detail: string,
  ) {
    super(`条件表达式无法解析：「${source}」—— ${detail}`);
    this.name = "ConditionSyntaxError";
  }
}

const COMPARATORS = ["==", "!=", ">=", "<=", ">", "<", "includes"] as const;
type Comparator = (typeof COMPARATORS)[number];

/* ── 端点与字面量 ─────────────────────────────────────────── */

/**
 * `test_tube_1.mouth` → `{ objectId, portId }`。
 * 端口还是槽位在解析时分不出来（同一个语法位置），所以两边都填，
 * 由 `endpointMatches` 的语义决定：查询只声明其一即可匹配。
 */
function parseEndpoint(raw: string, source: string): { byPort: Endpoint; bySlot: Endpoint } {
  const text = raw.trim();
  if (text.length === 0) throw new ConditionSyntaxError(source, "端点为空");

  const dot = text.indexOf(".");
  if (dot < 0) {
    return { byPort: { objectId: text }, bySlot: { objectId: text } };
  }
  const objectId = text.slice(0, dot);
  const anchorId = text.slice(dot + 1);
  return {
    byPort: { objectId, portId: anchorId },
    bySlot: { objectId, slotId: anchorId },
  };
}

/** 字面量：数字、true/false、null、带引号或不带引号的字符串。 */
function parseLiteral(raw: string): unknown {
  const text = raw.trim();
  if (text === "true") return true;
  if (text === "false") return false;
  if (text === "null") return null;
  if (/^-?\d+(\.\d+)?$/.test(text)) return Number(text);
  const quoted = /^["'](.*)["']$/.exec(text);
  return quoted ? quoted[1] : text;
}

/** 拆函数调用的参数，不支持嵌套括号（文法里也没有嵌套）。 */
function splitArgs(inside: string): string[] {
  return inside
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function callArgs(text: string, fnName: string): string[] | null {
  const prefix = `${fnName}(`;
  if (!text.startsWith(prefix) || !text.endsWith(")")) return null;
  return splitArgs(text.slice(prefix.length, -1));
}

/* ── 取值 ─────────────────────────────────────────────────── */

function readPath(root: unknown, path: string[]): unknown {
  let current: unknown = root;
  for (const key of path) {
    if (current === null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

type Reader = (context: EvalContext) => unknown;

function compileReader(text: string, source: string): Reader | null {
  // object(<id>).state.<路径>
  const objectMatch = /^object\(([^)]+)\)\.state\.(.+)$/.exec(text);
  if (objectMatch) {
    const objectId = objectMatch[1]!.trim();
    const path = objectMatch[2]!.split(".");
    return (context) => {
      const node = context.graph.nodes[objectId];
      if (!node) return undefined;
      return readPath(node.state, path);
    };
  }

  // object(<id>).pose.<字段>
  const poseMatch = /^object\(([^)]+)\)\.pose\.(.+)$/.exec(text);
  if (poseMatch) {
    const objectId = poseMatch[1]!.trim();
    const path = poseMatch[2]!.split(".");
    return (context) => {
      const node = context.graph.nodes[objectId];
      if (!node) return undefined;
      return readPath(node.pose, path);
    };
  }

  // state(<域>.<键>)
  const stateMatch = /^state\(([^)]+)\)$/.exec(text);
  if (stateMatch) {
    const parts = stateMatch[1]!.trim().split(".");
    if (parts.length < 2) throw new ConditionSyntaxError(source, "state() 需要「域.键」两段");
    const [domain, ...rest] = parts;
    return (context) => readPath(context.state[domain!], rest);
  }

  // process(<id>).state / process.<id>.state（两种写法都收，实验包里两种都出现过）
  const processMatch = /^process[.(]([^).]+)\)?\.state$/.exec(text);
  if (processMatch) {
    const processId = processMatch[1]!.trim();
    return (context) => context.processes[processId]?.state;
  }

  const progressMatch = /^process[.(]([^).]+)\)?\.progress$/.exec(text);
  if (progressMatch) {
    const processId = progressMatch[1]!.trim();
    return (context) => context.processes[processId]?.progress;
  }

  return null;
}

/* ── 谓词 ─────────────────────────────────────────────────── */

function asRelationType(raw: string | undefined, source: string): RelationType | undefined {
  if (raw === undefined) return undefined;
  const text = String(parseLiteral(raw));
  if (!(RELATION_TYPES as readonly string[]).includes(text)) {
    throw new ConditionSyntaxError(
      source,
      `未知关系类型 "${text}"，可用：${RELATION_TYPES.join(", ")}`,
    );
  }
  return text as RelationType;
}

/** 气体通路可以经过的关系类型。装配链路是 insert → connect → collect。 */
const GAS_PATH_RELATIONS: readonly RelationType[] = ["insert", "connect", "collect", "place"];

/**
 * 两个端点在装配图上是否连通（只走 active 关系）。
 * 这是「装置搭好了没有」的核心查询 —— 判据是**关系图连通性**，不是坐标（§22 红线 1）。
 */
function pathExists(graph: AssemblyGraph, fromId: string, toId: string): boolean {
  if (fromId === toId) return true;
  const edges = new Map<string, string[]>();
  for (const relation of listRelations(graph)) {
    if (relation.state !== "active") continue;
    if (!GAS_PATH_RELATIONS.includes(relation.type)) continue;
    const a = relation.from.objectId;
    const b = relation.to.objectId;
    edges.set(a, [...(edges.get(a) ?? []), b]);
    edges.set(b, [...(edges.get(b) ?? []), a]);
  }

  const seen = new Set([fromId]);
  const queue = [fromId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === toId) return true;
    for (const next of edges.get(current) ?? []) {
      if (seen.has(next)) continue;
      seen.add(next);
      queue.push(next);
    }
  }
  return false;
}

function compilePredicate(text: string, source: string): CompiledCondition | null {
  const exists = callArgs(text, "relation.exists");
  if (exists) {
    if (exists.length < 2) throw new ConditionSyntaxError(source, "relation.exists 需要两个端点");
    const from = parseEndpoint(exists[0]!, source);
    const to = parseEndpoint(exists[1]!, source);
    const type = asRelationType(exists[2], source);
    return (context) =>
      hasRelation(context.graph, from.byPort, to.byPort, type) ||
      hasRelation(context.graph, from.bySlot, to.bySlot, type) ||
      hasRelation(context.graph, from.byPort, to.bySlot, type) ||
      hasRelation(context.graph, from.bySlot, to.byPort, type);
  }

  const path = callArgs(text, "relation.pathExists");
  if (path) {
    if (path.length < 2) throw new ConditionSyntaxError(source, "relation.pathExists 需要两个端点");
    const fromId = parseEndpoint(path[0]!, source).byPort.objectId;
    const toId = parseEndpoint(path[1]!, source).byPort.objectId;
    return (context) => pathExists(context.graph, fromId, toId);
  }

  const event = callArgs(text, "event.exists");
  if (event) {
    if (event.length !== 1) throw new ConditionSyntaxError(source, "event.exists 需要一个事件名");
    const name = String(parseLiteral(event[0]!));
    return (context) => context.events.has(name);
  }

  return null;
}

/* ── 比较 ─────────────────────────────────────────────────── */

function findComparator(text: string): { op: Comparator; left: string; right: string } | null {
  // 先找 includes（词法上是单词，不能被 < 之类误切），再按长度找符号
  const includesAt = text.indexOf(" includes ");
  if (includesAt > 0) {
    return {
      op: "includes",
      left: text.slice(0, includesAt),
      right: text.slice(includesAt + " includes ".length),
    };
  }
  for (const op of ["==", "!=", ">=", "<="] as const) {
    const at = text.indexOf(op);
    if (at > 0) return { op, left: text.slice(0, at), right: text.slice(at + op.length) };
  }
  for (const op of [">", "<"] as const) {
    const at = text.indexOf(op);
    if (at > 0) return { op, left: text.slice(0, at), right: text.slice(at + op.length) };
  }
  return null;
}

function compare(op: Comparator, actual: unknown, expected: unknown): boolean {
  switch (op) {
    case "==":
      return actual === expected || String(actual) === String(expected);
    case "!=":
      return !(actual === expected || String(actual) === String(expected));
    case ">=":
      return Number(actual) >= Number(expected);
    case "<=":
      return Number(actual) <= Number(expected);
    case ">":
      return Number(actual) > Number(expected);
    case "<":
      return Number(actual) < Number(expected);
    case "includes":
      if (Array.isArray(actual)) {
        return actual.some((item) => item === expected || String(item) === String(expected));
      }
      return typeof actual === "string" && actual.includes(String(expected));
  }
}

/* ── 编译入口 ─────────────────────────────────────────────── */

/**
 * 编译一条条件。**解析不了就抛 ConditionSyntaxError**，绝不返回恒 false 的函数。
 */
export function compileCondition(source: string): CompiledCondition {
  const text = source.trim();
  if (text.length === 0) throw new ConditionSyntaxError(source, "空条件");

  /*
   * 取反：`!relation.exists(...)`。
   *
   * 没有它就写不出「导管已经不在水里了」这类条件 —— 规则只能把状态置上去，
   * 置不回来。氧气实验第 9 步「先撤导管再熄灯」正好卡在这：`inWater` 被置 true 后
   * 永远回不到 false，撤除步骤永远判不过（`bug/023-化学/002-bug.md`「跑不完闭环」）。
   */
  if (text.startsWith("!")) {
    const inner = compileCondition(text.slice(1));
    return (context) => !inner(context);
  }

  const predicate = compilePredicate(text, source);
  if (predicate) return predicate;

  const comparison = findComparator(text);
  if (!comparison) {
    throw new ConditionSyntaxError(
      source,
      `既不是谓词（relation.exists / relation.pathExists / event.exists），` +
        `也不含比较符（${COMPARATORS.join(" ")}）`,
    );
  }

  const reader = compileReader(comparison.left.trim(), source);
  if (!reader) {
    throw new ConditionSyntaxError(
      comparison.left.trim(),
      "取值只支持 object(<id>).state.<路径> / object(<id>).pose.<字段> / state(<域>.<键>) / process(<id>).state",
    );
  }

  const expected = parseLiteral(comparison.right);
  const op = comparison.op;
  return (context) => compare(op, reader(context), expected);
}

/** 编译一组条件，全部满足才为真（§6.2 的 `when` 是 AND 语义）。 */
export function compileAll(sources: readonly string[]): CompiledCondition {
  const compiled = sources.map((source) => compileCondition(source));
  return (context) => compiled.every((condition) => condition(context));
}

/** 供 pack-validate 用：只解析不求值，用来在出包前发现写错的条件。 */
export function assertParsable(sources: readonly string[]): void {
  for (const source of sources) compileCondition(source);
}
